<template>
  <div
    v-if="open"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    @click.self="$emit('close')"
  >
    <div class="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
      <!-- Metadata top bar -->
      <div class="px-5 py-3.5 border-b border-gray-100 bg-gray-50/60 rounded-t-lg">
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <h3 class="text-sm font-semibold text-gray-800 truncate">{{ meta.title }}</h3>
            <div class="mt-1.5 flex items-center gap-x-2 gap-y-1 flex-wrap text-[11px]">
              <span v-if="meta.date" class="font-medium text-gray-600">{{ meta.date }}</span>
              <span v-if="meta.type" class="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">{{ meta.type }}</span>
              <span v-if="meta.channel" class="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{{ meta.channel }}</span>
              <span v-if="meta.status" class="px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{{ meta.status }}</span>
              <span v-if="participantsSummary" class="text-gray-500 truncate">{{ participantsSummary }}</span>
            </div>
            <div v-if="meta.topics.length" class="mt-1.5 flex items-center gap-1 flex-wrap">
              <span
                v-for="(t, i) in meta.topics"
                :key="i"
                class="text-[10px] px-1.5 py-0.5 rounded bg-white border border-gray-200 text-gray-500"
              >{{ t }}</span>
            </div>
          </div>
          <button class="text-gray-400 hover:text-gray-600 text-sm leading-none shrink-0" @click="$emit('close')">✕</button>
        </div>

        <!-- Source tabs — a Diverge engagement's content can live in the transcript OR the note
             (PLs often paste recap/transcript content into the note). Show whichever exist and open
             to the one the cited quote came from. -->
        <div v-if="!loading && !error && tabs.length > 1" class="mt-2.5 flex items-center gap-1">
          <button
            v-for="t in tabs"
            :key="t.key"
            class="px-2.5 py-1 text-[11px] font-medium rounded-md border"
            :class="activeTab === t.key
              ? 'border-accent text-accent bg-accent-50'
              : 'border-gray-200 text-gray-500 hover:bg-gray-50'"
            @click="activeTab = t.key"
          >{{ t.label }}</button>
        </div>
      </div>

      <div ref="body" class="overflow-y-auto px-5 py-4 text-xs">
        <LoadingIndicator v-if="loading" message="Loading engagement..." />
        <p v-else-if="error" class="text-gray-500 py-6 text-center">{{ error }}</p>
        <p v-else-if="!tabs.length" class="text-gray-500 py-6 text-center">No transcript or note available for this engagement.</p>

        <!-- Transcript, timestamped source (parsed WebVTT cues) — matching cue highlighted -->
        <div v-else-if="activeTab === 'transcript' && cues.length" class="space-y-1">
          <div
            v-for="(cue, i) in cues"
            :key="i"
            :ref="(el) => setCueRef(i, el)"
            class="flex gap-2 rounded px-1.5 py-1"
            :class="cue.hl ? 'bg-yellow-100' : ''"
          >
            <span class="text-gray-300 tabular-nums whitespace-nowrap">{{ cue.ts }}</span>
            <span class="min-w-0">
              <span v-if="cue.speaker" class="font-semibold text-gray-600">{{ cue.speaker }}: </span>
              <span class="text-gray-700">{{ cue.text }}</span>
            </span>
          </div>
        </div>

        <!-- Plain-text source — a note (PL recap / email), or a transcript that carries no cues
             (meeting-bot text, a pasted Teams transcript, a typed one). Quote highlighted in place. -->
        <div v-else class="whitespace-pre-wrap leading-relaxed text-gray-700">
          <template v-for="(seg, i) in plainSegments" :key="i">
            <mark v-if="seg.hl" class="bg-yellow-100 rounded-sm">{{ seg.text }}</mark><span v-else>{{ seg.text }}</span>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import LoadingIndicator from "@/components/widgetcomponents/library/LoadingIndicator.vue";

const EMPTY_META = { title: "Transcript", date: "", type: "", channel: "", status: "", topics: [], participants: [] };

export default {
  name: "TranscriptModal",
  components: { LoadingIndicator },
  props: {
    open: { type: Boolean, default: false },
    engagementId: { type: Number, default: null },
    quote: { type: String, default: "" },
  },
  emits: ["close"],
  data() {
    // cues: the transcript when it's WebVTT. transcriptText: the transcript verbatim when it isn't
    // (only one of the two is ever populated).
    return { loading: false, error: "", cues: [], transcriptText: "", note: "", activeTab: "transcript", meta: { ...EMPTY_META } };
  },
  computed: {
    participantsSummary() {
      const p = this.meta.participants;
      if (!p.length) return "";
      if (p.length <= 3) return p.join(", ");
      return `${p.slice(0, 3).join(", ")} +${p.length - 3} more`;
    },
    // Which content tabs exist for this engagement (fixed order: Transcript, then Note). A transcript
    // counts whether or not it parsed into cues — a cue-less one still renders as text.
    tabs() {
      const t = [];
      if (this.cues.length || this.transcriptText.trim()) t.push({ key: "transcript", label: "Transcript" });
      if (this.note.trim()) t.push({ key: "note", label: "Note" });
      return t;
    },
    // The active plain-text blob split around the first matched quote fragment, so the quote can be
    // highlighted in place. Serves both plain-text sources (note, cue-less transcript).
    plainSegments() {
      const text = this.activeTab === "note" ? this.note : this.transcriptText;
      if (!text) return [];
      const span = this.findQuote(text);
      if (!span) return [{ text, hl: false }];
      return [
        { text: text.slice(0, span[0]), hl: false },
        { text: text.slice(span[0], span[1]), hl: true },
        { text: text.slice(span[1]), hl: false },
      ].filter((s) => s.text.length);
    },
  },
  watch: {
    open(val) {
      if (val && this.engagementId) this.load();
    },
    engagementId() {
      if (this.open && this.engagementId) this.load();
    },
    activeTab() {
      this.$nextTick(() => this.scrollToHighlight());
    },
  },
  created() {
    this._cueEls = {};
  },
  methods: {
    setCueRef(i, el) {
      if (el) this._cueEls[i] = el;
    },
    async load() {
      this.loading = true;
      this.error = "";
      this.cues = [];
      this.transcriptText = "";
      this.note = "";
      this.meta = { ...EMPTY_META };
      this._cueEls = {};
      try {
        const { data } = await axios.get(`/api/engagements/${this.engagementId}/`);
        this.meta = {
          title: data.subject || data.name || "Transcript",
          date: data.date
            ? new Date(data.date).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })
            : "",
          type: data.engagement_type_name || "",
          channel: data.engagement_channel_name || "",
          status: this.humanize(data.status),
          topics: data.engagement_topic_names || [],
          participants: (data.engagement_participants || []).map((p) => p.name).filter(Boolean),
        };
        const raw = data.engagement_transcript || data.zoomMeetingTranscription || "";
        this.cues = this.parseVtt(raw);
        // Not every transcript is WebVTT — meeting-bot output, a transcript pasted out of the Teams
        // pane, and typed ones carry no cues at all. Keep the raw text so it still renders (this is
        // what the engagement Overview's transcript modal has always done) instead of reporting the
        // engagement as having no content.
        this.transcriptText = this.cues.length ? "" : this.stripPuaGlyphs(raw);
        this.note = data.note || "";
        this.activeTab = this.pickDefaultTab();
        this.highlight();
      } catch (e) {
        console.error("Error loading engagement:", e);
        this.error = "Could not load the engagement.";
      } finally {
        this.loading = false;
      }
    },
    // Open to the tab the quote actually came from: prefer the source that CONTAINS the quote
    // (transcript first), else whichever content exists.
    pickDefaultTab() {
      const frags = this.quoteFragments();
      const hit = (text) => {
        const nt = this.norm(text);
        return frags.length > 0 && frags.some((f) => nt.includes(f));
      };
      const transcript = this.cues.length ? this.cues.map((c) => c.text).join(" ") : this.transcriptText;
      if (transcript && hit(transcript)) return "transcript";
      if (this.note.trim() && hit(this.note)) return "note";
      if (transcript.trim()) return "transcript";
      return this.note.trim() ? "note" : "transcript";
    },
    humanize(s) {
      return s ? s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "";
    },
    parseVtt(raw) {
      if (!raw) return [];
      const body = raw.replace(/^WEBVTT[\s\S]*?\r?\n\r?\n/, "");
      const cues = [];
      for (const block of body.split(/\r?\n\r?\n/)) {
        const lines = block.split(/\r?\n/).filter((l) => l.trim() !== "");
        const tsLine = lines.find((l) => /-->/.test(l));
        if (!tsLine) continue;
        const m = tsLine.match(/(\d{2}:\d{2}:\d{2})[.,]\d{3}/);
        let text = lines.filter((l) => !/-->/.test(l) && !/^\d+$/.test(l.trim())).join(" ").trim();
        let speaker = null;
        const vm = text.match(/<v\s+([^>]+?)>([\s\S]*)/i);
        if (vm) {
          speaker = vm[1].trim();
          text = vm[2];
        }
        text = text.replace(/<[^>]+>/g, "").trim();
        if (text) cues.push({ ts: m ? m[1] : "", speaker, text, hl: false });
      }
      return cues;
    },
    norm(s) {
      return (s || "").replace(/\s+/g, " ").trim().toLowerCase();
    },
    // A transcript pasted out of the Teams transcript pane drags Teams' icon-font characters along
    // with it (Unicode private-use area, U+E000–U+F8FF); nothing maps them, so they render as tofu.
    stripPuaGlyphs(s) {
      return (s || "").replace(/[\uE000-\uF8FF]/g, "");
    },
    // The evidence quote is formatted `Speaker: "…"` and is usually a trimmed slice of a
    // longer cue/note span (sometimes stitched across non-adjacent spans with "…").
    quoteFragments() {
      const cleaned = this.quote
        .replace(/^\s*[^:"]{1,40}:\s*/, "") // leading "Speaker Name:" label
        .replace(/[“”‘’"]/g, ""); // wrapping / stray quote marks
      return cleaned.split(/\.\.\.|…/).map((f) => this.norm(f)).filter((f) => f.length >= 15);
    },
    // Locate the quote inside a plain-text blob (note, or a cue-less transcript), tolerating
    // whitespace differences — the quote arrives with runs of space and line breaks collapsed, which
    // the source still has, so a straight indexOf misses roughly one in eight. Match on a normalized
    // copy that remembers where each character came from, and return [start, end) in the RAW text so
    // the highlight lands on the original characters. Null if no fragment matches.
    findQuote(text) {
      const frags = this.quoteFragments();
      if (!frags.length) return null;
      let normed = "";
      const rawAt = [];   // offset in `normed` -> offset in `text`
      let atSpace = true; // collapse runs of whitespace, drop the leading run — mirrors norm()
      for (let i = 0; i < text.length; i++) {
        const isSpace = /\s/.test(text[i]);
        if (isSpace && atSpace) continue;
        normed += isSpace ? " " : text[i].toLowerCase();
        rawAt.push(i);
        atSpace = isSpace;
      }
      for (const f of frags) {
        const at = normed.indexOf(f);
        if (at >= 0) return [rawAt[at], rawAt[at + f.length - 1] + 1];
      }
      return null;
    },
    highlight() {
      const frags = this.quoteFragments();
      if (frags.length) {
        this.cues.forEach((c) => {
          const nt = this.norm(c.text);
          if (frags.some((f) => nt.includes(f))) c.hl = true;
        });
      }
      this.$nextTick(() => this.scrollToHighlight());
    },
    // Bring the quote into view: the matching cue on the WebVTT path, the <mark> on the plain-text one.
    scrollToHighlight() {
      const cue = this.cues.findIndex((c) => c.hl);
      const el = cue >= 0
        ? this._cueEls[cue]
        : this.$refs.body && this.$refs.body.querySelector("mark");
      if (el) el.scrollIntoView({ block: "center" });
    },
  },
};
</script>
